import { useEffect, useState, type ChangeEvent } from 'react'
import { useForm } from 'react-hook-form'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSaveDatabaseConnector } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { useUploadWorkflowRunFiles } from '@/hooks/useWorkflowRunFiles'
import { DATABASE_ENGINES, DATABASE_ENCRYPT_MODES, FILE_BASED_ENGINES } from '@/types'
import type { Connector } from '@/types'
import { parseDatabaseMetadata } from './databaseConnectorMetadata'

const SQL_SERVER_ENGINES = ['sqlserver', 'cloud-sql-mssql']

// The upload endpoint is shared with the file-upload trigger input, which legitimately accepts
// documents, so the restriction lives here rather than server-side. An OpenEdge dump is checked by
// name because content-sniffing would not help: a .pptx is itself a zip, so it passes the
// `unzip -t` gate in fetch-dump.sh and only fails much later with a confusing "no .df files" error.
const ACCEPTED_FILE_EXTENSIONS: Record<string, string[]> = {
  openedge: ['.zip'],
  sqlite: ['.sqlite', '.db', '.sqlite3'],
}

const acceptedExtensionsFor = (dbType: string): string[] =>
  ACCEPTED_FILE_EXTENSIONS[dbType] ?? ACCEPTED_FILE_EXTENSIONS['sqlite']

const ENCRYPT_LABELS: Record<string, string> = {
  true: 'Require (validate certificate)',
  disable: 'Disable (local / self-signed cert)',
}

interface FormValues {
  connectionName: string
  dbType: string
  host: string
  port: string
  database: string
  user: string
  password: string
  encrypt: string
  fileBlobPath: string
  fileName: string
}

const EMPTY_FORM: FormValues = {
  connectionName: '',
  dbType: 'postgresql',
  host: '',
  port: '',
  database: '',
  user: '',
  password: '',
  encrypt: 'true',
  fileBlobPath: '',
  fileName: '',
}

interface DatabaseConnectionDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  existing?: Connector | null
  onSaved?: (connectorId: string) => void
}

export function DatabaseConnectionDialog({
  projectId,
  open,
  onOpenChange,
  existing,
  onSaved,
}: Readonly<DatabaseConnectionDialogProps>) {
  const save = useSaveDatabaseConnector(projectId)
  const upload = useUploadWorkflowRunFiles(projectId)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onChange', defaultValues: EMPTY_FORM })

  useEffect(() => {
    if (!open) return
    const metadata = parseDatabaseMetadata(existing?.providerMetadata ?? null)
    reset(metadata
      ? { connectionName: existing?.connectionName ?? '', dbType: metadata.dbType, host: metadata.host, port: metadata.port ?? '', database: metadata.database, user: metadata.user, password: '', encrypt: metadata.encrypt || 'true', fileBlobPath: metadata.fileBlobPath ?? '', fileName: metadata.fileName ?? '' }
      : EMPTY_FORM)
  }, [open, existing, reset])

  const [fileTypeError, setFileTypeError] = useState<string | null>(null)

  const dbType = watch('dbType')
  const encrypt = watch('encrypt')
  const isFileBased = FILE_BASED_ENGINES.includes(dbType)
  const isSqlServer = SQL_SERVER_ENGINES.includes(dbType)

  const onFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // `accept` is only a picker hint — "All files" bypasses it — so check before spending the upload.
    const accepted = acceptedExtensionsFor(dbType)
    if (!accepted.some((extension) => file.name.toLowerCase().endsWith(extension))) {
      setFileTypeError(`${file.name} is not a ${accepted.join(' / ')} file.`)
      e.target.value = ''
      return
    }

    setFileTypeError(null)
    try {
      const res = await upload.mutateAsync([file])
      const uploaded = res.files[0]
      setValue('fileBlobPath', uploaded.blobPath, { shouldValidate: true })
      setValue('fileName', uploaded.fileName, { shouldValidate: true })
    } catch {
      // Surfaced to the user through upload.isError below.
    }
  }

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset(EMPTY_FORM)
      save.reset()
      upload.reset()
      setFileTypeError(null)
    }
    onOpenChange(nextOpen)
  }

  const onSubmit = (data: FormValues) => {
    save.mutate(
      {
        connectorId: existing?.id ?? null,
        connectionName: existing ? existing.connectionName : data.connectionName.trim(),
        dbType: data.dbType,
        host: isFileBased ? '' : data.host,
        port: data.port ? data.port : null,
        database: isFileBased ? data.fileName : data.database,
        user: isFileBased ? '' : data.user,
        password: isFileBased ? '' : data.password,
        encrypt: isSqlServer ? data.encrypt : null,
        fileBlobPath: isFileBased ? data.fileBlobPath : null,
        fileName: isFileBased ? data.fileName : null,
      },
      {
        onSuccess: (response) => {
          onSaved?.(response.connectorId)
          handleClose(false)
        },
      },
    )
  }

  const inlineError = save.isError
    ? (extractValidationMessage(save.error) ?? getErrorMessage(save.error) ?? 'Failed to save database connection.')
    : null

  const uploadError = fileTypeError
    ?? (upload.isError
      ? (extractValidationMessage(upload.error) ?? getErrorMessage(upload.error) ?? 'File upload failed — check the file type and size.')
      : null)

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit database connection' : 'Add database connection'}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.stopPropagation()
            handleSubmit(onSubmit)(e)
          }}
          className="space-y-4"
        >
          {!existing && (
            <div className="space-y-2">
              <Label htmlFor="db-connection-name">
                Connection Name <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="db-connection-name"
                data-testid="db-dialog-connection-name"
                type="text"
                placeholder="e.g. Orders source"
                {...register('connectionName', {
                  required: 'Connection name is required',
                  validate: (value) => value.trim().length > 0 || 'Connection name is required',
                })}
              />
              <p className="text-sm text-muted-foreground">Must be unique for this connector type.</p>
              {errors.connectionName && <p role="alert" className="text-sm text-destructive">{errors.connectionName.message}</p>}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="db-engine">Engine</Label>
            <Select value={dbType} onValueChange={(v) => setValue('dbType', v, { shouldValidate: true })}>
              <SelectTrigger id="db-engine" data-testid="db-dialog-engine">
                <SelectValue placeholder="Select engine..." />
              </SelectTrigger>
              <SelectContent>
                {DATABASE_ENGINES.map((engine) => (
                  <SelectItem key={engine} value={engine}>{engine}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" {...register('dbType', { required: true })} />
          </div>

          {isSqlServer && (
            <div className="space-y-2">
              <Label htmlFor="db-encrypt">Encryption</Label>
              <Select value={encrypt} onValueChange={(v) => setValue('encrypt', v, { shouldValidate: true })}>
                <SelectTrigger id="db-encrypt" data-testid="db-dialog-encrypt">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATABASE_ENCRYPT_MODES.map((mode) => (
                    <SelectItem key={mode} value={mode}>{ENCRYPT_LABELS[mode] ?? mode}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" {...register('encrypt')} />
              <p className="text-sm text-muted-foreground">
                Use &ldquo;Disable&rdquo; for a local or on-prem SQL Server with a self-signed certificate.
              </p>
            </div>
          )}

          {isFileBased ? (
            <div className="space-y-2">
              <Label htmlFor="db-file">{dbType === 'openedge' ? 'OpenEdge dump (.zip)' : 'SQLite file'}</Label>
              <Input
                id="db-file"
                data-testid="db-dialog-file"
                type="file"
                accept={acceptedExtensionsFor(dbType).join(',')}
                onChange={onFileSelected}
              />
              <input type="hidden" {...register('fileBlobPath', { required: 'A data file is required' })} />
              {upload.isPending && <p className="text-sm text-muted-foreground">Uploading…</p>}
              {!upload.isPending && !uploadError && watch('fileName') && (
                <p className="text-sm text-muted-foreground">Uploaded: {watch('fileName')}</p>
              )}
              {uploadError && <p role="alert" className="text-sm text-destructive">{uploadError}</p>}
              {errors.fileBlobPath && !uploadError && <p role="alert" className="text-sm text-destructive">{errors.fileBlobPath.message}</p>}
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="db-database">Database</Label>
              <Input
                id="db-database"
                data-testid="db-dialog-database"
                type="text"
                placeholder="mydb"
                {...register('database', { required: 'Database is required' })}
              />
              {errors.database && <p role="alert" className="text-sm text-destructive">{errors.database.message}</p>}
            </div>
          )}

          {!isFileBased && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="db-host">Host</Label>
                  <Input
                    id="db-host"
                    data-testid="db-dialog-host"
                    type="text"
                    placeholder="db.example.com"
                    {...register('host', { required: 'Host is required' })}
                  />
                  {errors.host && <p role="alert" className="text-sm text-destructive">{errors.host.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="db-port">Port</Label>
                  <Input
                    id="db-port"
                    data-testid="db-dialog-port"
                    type="text"
                    placeholder="5432"
                    {...register('port')}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="db-user">User</Label>
                <Input
                  id="db-user"
                  data-testid="db-dialog-user"
                  type="text"
                  placeholder="db_user"
                  {...register('user', { required: 'User is required' })}
                />
                {errors.user && <p role="alert" className="text-sm text-destructive">{errors.user.message}</p>}
                <p className="text-sm text-muted-foreground">
                  For source databases, use a read-only account — the migration tooling never needs to write to the source.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="db-password">Password</Label>
                <Input
                  id="db-password"
                  data-testid="db-dialog-password"
                  type="password"
                  placeholder={existing ? 'Re-enter password' : 'Password'}
                  {...register('password', { required: 'Password is required' })}
                />
                {errors.password && <p role="alert" className="text-sm text-destructive">{errors.password.message}</p>}
                <p className="text-sm text-muted-foreground">
                  Editing this connection requires re-entering the password.
                </p>
              </div>
            </>
          )}

          {inlineError && <p role="alert" className="text-sm text-destructive">{inlineError}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || upload.isPending}>
              {upload.isPending ? 'Uploading...' : save.isPending ? 'Saving...' : existing ? 'Save changes' : 'Add connection'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
